import { encode, ParsedUrlQuery } from 'querystring';

import {
  TRACE_INFO,
  UTM_CAMPAIGN,
  UTM_MEDIUM,
  UTM_REFERRER_PARAM,
  UTM_SOURCE,
} from 'src/common/constants';
import { generateSlug } from 'src/common/generateSlug';
import { generateQueryParams } from 'src/common/links';
import { ROUTES } from 'src/common/routes';
import { JobLinkProps } from 'src/common/types/jobLinkProps';
import { SourcedJob } from 'src/global/models/SourcedJob';

export const getSourcedJobLinkProps = (
  sourcedJob: SourcedJob,
  currentURLSearchParams?: URLSearchParams
): JobLinkProps => {
  const id = sourcedJob.id;
  const title = generateSlug(sourcedJob.title);

  const utmReferrer = currentURLSearchParams?.get(UTM_REFERRER_PARAM);
  const utmMedium = currentURLSearchParams?.get(UTM_MEDIUM);
  const utmCampaign = currentURLSearchParams?.get(UTM_CAMPAIGN);
  const utmSource = currentURLSearchParams?.get(UTM_SOURCE);
  const traceInfo = currentURLSearchParams?.get(TRACE_INFO);

  const queryParams = generateQueryParams({
    utmMedium,
    utmCampaign,
    utmReferrer,
    utmSource,
    traceInfo,
  });
  return {
    href: {
      pathname: `/${ROUTES.sourcedJob}`,
      query: {
        id,
        title,
        utm_referrer: utmReferrer && utmReferrer,
        utm_campaign: utmCampaign && utmCampaign,
        utm_medium: utmMedium && utmMedium,
        utm_source: utmSource && utmSource,
        traceInfo: traceInfo && traceInfo,
      },
    },
    as: `/${ROUTES.sourcedJobs}/${title}/${id}${queryParams}`,
  };
};

export const getSourcedJobLinkPropsFromAsPath = (
  asPath: string
): JobLinkProps => {
  const urlParams = new URLSearchParams(asPath.split('?')[1]);

  const utmReferrer = urlParams.get(UTM_REFERRER_PARAM);
  const utmMedium = urlParams.get(UTM_MEDIUM);
  const utmCampaign = urlParams.get(UTM_CAMPAIGN);
  const utmSource = urlParams.get(UTM_SOURCE);
  const traceInfo = urlParams.get(TRACE_INFO);
  const sourcedJobTitle = urlParams.get('sJobTitle');
  const sourcedJobId = urlParams.get('id');

  const queryParams = generateQueryParams({
    utmMedium,
    utmCampaign,
    utmReferrer,
    utmSource,
    traceInfo,
  });
  return {
    href: {
      pathname: `/${ROUTES.sourcedJob}`,
      query: {
        id: sourcedJobId,
        title: sourcedJobTitle,
        utm_referrer: utmReferrer && utmReferrer,
        utm_campaign: utmCampaign && utmCampaign,
        utm_medium: utmMedium && utmMedium,
        utm_source: utmSource && utmSource,
        traceInfo: traceInfo && traceInfo,
      },
    },
    as: `/${ROUTES.sourcedJobs}/${sourcedJobTitle}/${sourcedJobId}${queryParams}`,
  };
};

export const getApplySourcedJobLinkProps = (
  sourcedJob: SourcedJob,
  queryParam: ParsedUrlQuery
) => {
  const urlQueryString = new URLSearchParams(encode(queryParam));
  const props = getSourcedJobLinkProps(sourcedJob, urlQueryString);
  let as: string;
  if (props.as.includes('?')) {
    as = props.as.replace('?', '/apply?');
  } else {
    as = `${props.as}/apply`;
  }

  return {
    as: as,
    href: props.href,
    query: queryParam,
  };
};

export const getYearsOfExperienceMessage = (
  minYearsOfExperience?: number,
  maxYearsOfExperience?: number
): { id: string; defaultMessage: string; values?: any } | undefined => {
  if (!minYearsOfExperience || !maxYearsOfExperience) return undefined;
  if (maxYearsOfExperience < 1) {
    return {
      id: 'job_detail_page.experience.less_than_a_year',
      defaultMessage: 'Less than a year of experience',
    };
  }
  if (minYearsOfExperience <= 10 && maxYearsOfExperience < 50) {
    return {
      id: 'job_detail_page.experience.min_and_max_years_experience',
      defaultMessage:
        '{minYearsOfExperience} - {maxYearsOfExperience} years of experience',
      values: { minYearsOfExperience, maxYearsOfExperience },
    };
  }
  if (minYearsOfExperience > 10) {
    return {
      id: 'job_detail_page.experience.more_than_ten_years',
      defaultMessage: 'More than 10 years of experience',
    };
  }
  return undefined;
};
